import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Fade,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  alpha,
  useTheme,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Stars as GoalIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';

const LevelStructure = () => {
  const theme = useTheme();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/api/levels/settings');
        if (response.data.status) {
          setSettings(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const levelData = [
    { rank: 'L1 - Associate', team: '30 Lakh', pct: '2%', self: '5 Lakh', reward: '10K Mobile DP', incentive: '5K', color: '#a855f7' },
    { rank: 'L2 - Sr Associate', team: '50 Lakh', pct: '3%', self: '15 Lakh', reward: '25K Laptop DP', incentive: '7.5K', color: '#8b5cf6' },
    { rank: 'L3 - Executive', team: '1 CR', pct: '4%', self: '25 Lakh', reward: '55K Bike DP', incentive: '15K', color: '#6366f1' },
    { rank: 'L4 - Team Leader', team: '2.5 CR', pct: '5%', self: '50 Lakh', reward: '1.5L Car DP', incentive: '20K', color: '#3b82f6' },
    { rank: 'L5 - Group Leader', team: '5 CR', pct: '6%', self: '1 CR', reward: '3L Car DP', incentive: '25K', color: '#0ea5e9' },
    { rank: 'L6 - Silver', team: '10 CR', pct: '7%', self: '2 CR', reward: '7L Car DP', incentive: '35K', color: '#94a3b8' },
    { rank: 'L7 - Gold', team: '25 CR', pct: '8%', self: '5 CR', reward: '10L Car DP', incentive: '50K', color: '#f59e0b' },
    { rank: 'L8 - Diamond', team: '50 CR', pct: '9%', self: '10 CR', reward: '15L Car DP', incentive: '75K', color: '#334155' },
    { rank: 'L9 - Platinum', team: '100 CR', pct: '10%', self: '25 CR', reward: '20L Car DP', incentive: '1 Lakh', color: '#475569' },
    { rank: 'L10 - Crown', team: '200 CR', pct: '11%', self: '50 CR', reward: '30L Car DP', incentive: '2 Lakh', color: '#1e293b' },
    { rank: 'L11 - Elite Club', team: '400 CR', pct: '12%', self: '100 CR', reward: '50L Car DP', incentive: '3 Lakh', color: '#0f172a' },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const payoutMonths = settings?.incentiveSettings?.payoutMonths || 1;

  return (
    <Box sx={{ p: { xs: 3, md: 6 }, bgcolor: '#f8fafc', minHeight: '100vh', display: 'flex', justifyContent: 'center' }}>
      <Fade in timeout={500}>
        <Box sx={{ maxWidth: 1200, width: '100%' }}>

          {/* Header */}
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TrophyIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1f2c' }}>
                Level Structure
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Performance ranks and business targets for the associate network.
              </Typography>
            </Box>
          </Box>

          {/* Special Rule Note */}
          <Box sx={{ mb: 4, p: 2, bgcolor: alpha('#f59e0b', 0.08), borderRadius: 3, border: '1px solid #f59e0b33', display: 'flex', alignItems: 'center', gap: 2 }}>
            <GoalIcon sx={{ color: '#d97706' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#92400e' }}>
              Special Rule: Complete the <strong>Self Business Target</strong> to unlock guaranteed incentives for {payoutMonths} consecutive months.
            </Typography>
          </Box>

          {/* List Style Matrix */}
          <Paper elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
            <List sx={{ p: 0 }}>
              {levelData.map((level, index) => (
                <React.Fragment key={level.rank}>
                  <ListItem sx={{
                    py: 3,
                    px: 4,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 2,
                    '&:hover': { bgcolor: '#f8fafc' }
                  }}>
                    <Box sx={{ minWidth: 160 }}>
                      <Typography sx={{ fontWeight: 800, color: level.color, fontSize: '0.95rem' }}>
                        {level.rank}
                      </Typography>
                      <Chip
                        label={level.pct}
                        size="small"
                        sx={{ mt: 0.5, fontWeight: 700, height: 20, bgcolor: alpha(level.color, 0.1), color: level.color }}
                      />
                    </Box>

                    <Box sx={{ flex: 1, display: 'flex', gap: 4 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Team Target</Typography>
                        <Typography sx={{ fontWeight: 600 }}>{level.team}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Self Target</Typography>
                        <Typography sx={{ fontWeight: 600 }}>{level.self}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Reward & Incentive</Typography>
                      <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {level.reward} <Box component="span" sx={{ color: 'primary.main', mx: 1 }}>+</Box> ₹{level.incentive}
                      </Typography>
                    </Box>

                    <ChevronRightIcon sx={{ color: 'divider', display: { xs: 'none', md: 'block' } }} />
                  </ListItem>
                  {index < levelData.length - 1 && <Divider sx={{ mx: 4 }} />}
                </React.Fragment>
              ))}
            </List>
          </Paper>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Operational Status: Level goals are synchronized across all payout cycles.
            </Typography>
          </Box>

        </Box>
      </Fade>
    </Box>
  );
};

export default LevelStructure;
